import { JSX } from 'preact';
import { useCallback, useMemo } from 'preact/hooks';
import type { Locale } from '@schibsted-svp/web-player';

import { useLocaleContext } from './LocaleProvider';

export type Namespace = Record<Exclude<Locale, 'en'>, Record<string, string>>;

const interpolate = (
  translation: string,
  interpolationMap: Record<string, unknown>
): string => {
  return Object.keys(interpolationMap).reduce((acc, key) => {
    return acc.replace(
      new RegExp(`{{${key}}}`, 'g'),
      interpolationMap[key] as string
    );
  }, translation);
};

export type TFunction = <NoJSX extends boolean = false>(
  key: string,
  interpolationMap?: Record<string, unknown>,
  options?: { noJSX?: NoJSX }
) => NoJSX extends true ? string : JSX.Element | string;

export interface i18n {
  t: TFunction;
  locale: Exclude<Locale, 'en'>;
}

export const useTranslation = (ns: Namespace) => {
  const { locale } = useLocaleContext();

  const t = useCallback<TFunction>(
    (key, interpolationMap = {}, { noJSX } = {}) => {
      const namespace = ns[locale] || {};

      if (key in namespace) {
        const translation = interpolate(namespace[key], interpolationMap);

        if (noJSX) {
          return translation;
        }

        return (
          <span
            dangerouslySetInnerHTML={{
              __html: translation,
            }}
          />
        ) as unknown as string;
      }

      return key;
    },
    [ns, locale]
  );

  const i18n = useMemo(() => {
    return {
      locale,
      t,
    };
  }, [locale, t]);

  return { t, i18n };
};
